import '@sass/showresult/showresult.scss';
import { EventDispatcher, bindIntersectionObserver, classList, copyToClipboard, deepCopy, documentReady, gebi, genUID, getTimeText, getUrlVarBase64, getUrlVars, hideElement, isEmptyObject, isFullScreen, isMobileView, isNetworkError, l, last, lc, logEvent, qs, qsa, removeEmptyEntriesFromObject, setUrlParameter, showElement, slideDown, slideToggle, storedItemCreate, storedItemRead, tabTrappingHelper, toaster, urlParameterDelete, urlParameterGet, urlParameterSet } from '@utils/toolbox';
import { dateFormat, dateGetDate, dateGetMonth, dateParseISO } from '@utils/dateFns';
import { eventCategories, events } from '@js/utils/event';
import { Base64 } from '@js/plugins/base64';
import { Civet } from '@js/plugins/civet';
import { DateSelector } from '@components/dateselector';
import { DisplayMessage } from '@components/displaymessage';
import { Favorite } from '@js/components/favorite/favorite';
import { Filters } from '@module/Filters/Filters';
import { KalleBakom } from '@js/components/kallebakom';
import { PriceCalendar } from '@module/PriceCalendar/PriceCalendar';
import { PriceCutter } from '@module/PriceCutter/PriceCutter';
import { PriceWatcher } from '@module/PriceWatcher/PriceWatcher';
import { SearchBox } from '@module/SearchBox/SearchBox';
import { defaultFilterData } from '@utils/defaultFilterData';
import { resultsFetcher } from '@components/resultsFetcher';
import { resultsRender } from '@components/resultsRender';
import { timeline } from '@components/timeline';
import { timetable } from '@components/timetable';
import { trackEvent } from '@utils/googleDataLayer';
import { TooltipDialog } from '@js/components/tooltipDialog';

const { urlVars, timestamp, sharedOrgIndex } = js_params;
const defaultSorting = urlVars?.sorting || js_params.defaultSorting;

/**
 * @typedef {Object} searchBoxData
 * @property {String} searchBoxData.departDate
 * @property {String} searchBoxData.from
 * @property {String} searchBoxData.fromIata
 * @property {Array} searchBoxData.fromIatas
 * @property {Boolean} searchBoxData.fromMetro
 * @property {String} searchBoxData.returnDate
 * @property {String} searchBoxData.to
 * @property {String} searchBoxData.toIata
 * @property {Array} searchBoxData.toIatas
 * @property {Boolean} searchBoxData.toMetro
 */

/**
 * @typedef {Object} flight
 * @property {Number} best
 * @property {Number} cabin
 * @property {Number} climateCompensationPrice
 * @property {String} climateCompensationUrl
 * @property {Number} compensated_emission
 * @property {Number} emission
 * @property {Number} lowestprice
 * @property {Number} orgIndex
 * @property {String} promotedAgent
 * @property {String} promotedProduct
 * @property {Number} raw_emission
 * @property {Number} traveltime
 * @property {Array} trips
 */

const showresult = {
	// TODO: refactor duplicate variable (renderResults)
	searchParams: {
		page: 0,
		id: urlVars.id,
		timestamp: timestamp,
		sorting: defaultSorting,
		filters: getUrlVarBase64('filter'),
		sharedOrgIndex: sharedOrgIndex,
		initial: true
	},
	previousSorting: defaultSorting,
	isBestSortingInfoRead: false,
	isMobile: isMobileView(),
	// TODO: refactor duplicate variable (renderResults)
	isYouthSearch: js_params?.isYouthSearch,
	elems: {
		header: qs('header'),
		footer: gebi('menu-sorting-footer'),
		hero: gebi('ab-price-calendar-hero'),
		sortBars: qsa('.abTabNav--sortResult [data-sort]'),
		searchBoxWrapper: gebi('ab-showresult-search-box'),
		resultsColumn: gebi('results-column'),
		scrollToTop: gebi('ab-scroll-to-top'),
		loadingSpinner: qs('.loading-spinner__container'),
		flexTicketCheckbox: qs('[data-filterkey="flexTicket"] input'),
		climateCheckbox: qs('input#include-climate-compensation'),
		resultsContainer: qs('#results-container')
	},
	/**
	 * @type {KalleBakom}
	 */
	kalleBakom: null,
	/**
	 * @type {Favorite}
	 */
	favorite: null,
	/**
	 * @type {Filters}
	 */
	filters: null,
	/**
	 * @type {DisplayMessage}
	 */
	// displayMessage: null,
	/**
	 * @type {PriceCalendar}
	 */
	priceCalendar: null,
	/**
	 * @type {DateSelector}
	 */
	/**
	 * @type {PriceWatcher}
	 */
	priceWatcher: null,
	dateSelector: null,
	/**
	 * @type {PriceCutter}
	 */
	priceCutter: null,
	activeView: 'list',
	networkErrorCount: 0,
	async init () {
		this.initMain();
		resultsRender.init();
		this.getSavedFilters();
		this.initSearchBox();
		this.initFavorite();
		this.initEvents();
		this.initFilters();
		this.initFilterBanner();
		this.setSortingTabs(this.searchParams.sorting);

		// If invalid search then relax ^_^
		if (!this.isValidSearch() && !urlVars?.forceNearbyDates) {
			return;
		}

		switch (js_params.isupdating) {
			case 0: // Search complete
				await this.resultsGetFirst();
				break;
			case 1: // Search on going
				// The promise chain is quite deep here, be careful not to break it
				// await searchBox.continueSearch ->  await searchBox._callbacks.onSearchComplete -> await showresult.resultsPrepareRequest > await showresult.resultsGetFirst > await showresult.resultsGet
				await this.searchBox.continueSearch(js_params);
				break;
			// case 2: // Search failed
			// Handled by this.isValidSearch()
		}

		this.initHero();
		this.initDateSelector();
		this.initPriceCalendar();
		this.initPriceCutter();
		this.initPriceWatcher();
		this.initIntersectionObserver();

		// track price warnings
		if (gebi('price-warning')) {
			if (gebi('price-warning').classList.contains('result-box-warning--too-old')) {
				logEvent(events.observeAncientSearch, eventCategories.observe);
			} else if (gebi('price-warning').classList.contains('result-box-warning--old')) {
				logEvent(events.observeOldSearch, eventCategories.observe);
			}
		}

		// Initialise Timeline and Timetable
		timeline.init();
		timetable.init();
		if (urlVars?.forceNearbyDates) {
			hideElement('#ab-showresult-date-selector [data-show="pricecutter"]');
			qsa(`#ab-showresult-date-selector .${this.dateSelector.selectors.closeBtn}`).forEach((el) => {
				el.addEventListener('click', () => {
					window.history.go(-1);
				});
			});
		}
		this.initWarningMessage();

		if (urlVars?.debugKalle) {
			this.kalleBakomShow(0);
		}
		this.initWCAG();
	},
	initWarningMessage () {
		// If sorting is bestValue or if the user has not read the info about bestValue sorting and the domain is uberflieger.de then show the info
		if (this.searchParams.sorting === 'bestValue' || js_params.domain === 'uberflieger.de') {
			showElement('#bestSortingInfo');
		}
	},
	searchParamsForAPI (searchParams = this.searchParams) {
		const searchParamsCopy = removeEmptyEntriesFromObject(deepCopy(searchParams));

		if (!resultsRender.isSharedResultView) {
			delete searchParamsCopy?.sharedOrgIndex;
		}

		// Remove front only params
		delete searchParamsCopy?.filters?.activeTimetableBtns;
		delete searchParamsCopy?.filters?.sortEmission;
		delete searchParamsCopy?.filters?.collapsed;

		// Do not send youthTicket filters when no youth ticket are available
		// TODO: when is it possible to get this to fire? Documentation/testing needed
		if (searchParamsCopy.filters?.youthTicket && !this.isYouthSearch) {
			delete searchParamsCopy.filters?.youthTicket;
		}

		return searchParamsCopy;
	},
	setPersisentFiltersToUrl (url) {
		const { filters } = this.searchParamsForAPI();

		const checkIfLegsHaveChangedSinceLanding = () => {
			// If the user has only changed the date the append the currently applied filters to the URL
			const currentLegsLength = this.searchBox.data.legs.length;
			const currentLegsData = this.searchBox.data.legs.map((leg) => {
				// remove the property 'origin' as it's not present on js_params.showresultSearchParams
				const fromIatasWithoutOrigin = leg.fromIatas.map(({ origin, ...rest }) => rest);
				const toIatasWithoutOrigin = leg.toIatas.map(({ origin, ...rest }) => rest);
				return JSON.stringify(fromIatasWithoutOrigin) + JSON.stringify(toIatasWithoutOrigin);
			}).join(',');
			const previousLegsLength = js_params.showresultSearchParams.length;
			const previousLegsData = js_params.showresultSearchParams.map(leg => JSON.stringify(leg.fromIatas) + JSON.stringify(leg.toIatas)).join(',');
			const isSame = currentLegsLength === previousLegsLength && currentLegsData === previousLegsData;
			return isSame;
		};

		if (filters && Object.keys(filters).length) {
			const isSame = checkIfLegsHaveChangedSinceLanding();

			// Update baggageIncluded to match the current number of passengers
			if (filters?.baggageIncluded) {
				filters.baggageIncluded = resultsRender.numberOfBags;
			}

			if (!isSame) {
				return url;
			}


			// If single trip, only send one departure and arrival time
			if (this.searchBox.data.tripType === 0) {
				if (filters?.departureTime?.length) {
					filters.departureTime = filters.departureTime.filter(departureTimeFilter => departureTimeFilter.tripIndex === 0);
				}
				if (filters?.arrivalTime?.length) {
					filters.arrivalTime = filters.arrivalTime.filter(arrivalTimeFilter => arrivalTimeFilter.tripIndex === 0);
				}
			}

			// If return trip, only send two departure and arrival times
			if (this.searchBox.data.tripType === 1) {
				if (filters?.departureTime?.length) {
					filters.departureTime = filters.departureTime.filter(departureTimeFilter => departureTimeFilter.tripIndex === 0 || departureTimeFilter.tripIndex === 1);
				}
				if (filters?.arrivalTime?.length) {
					filters.arrivalTime = filters.arrivalTime.filter(arrivalTimeFilter => arrivalTimeFilter.tripIndex === 0 || arrivalTimeFilter.tripIndex === 1);
				}
			}


			// if departureTime or arrivalTime is an empty array, remove it
			if (filters?.departureTime?.length === 0) {
				delete filters.departureTime;
			}

			if (filters?.arrivalTime?.length === 0) {
				delete filters.arrivalTime;
			}

			// remove airportStops
			// TODO: remove this when the API is fixed to use a whitelist instead of a blacklist
			delete filters.airportStops;

			if (filters && Object.keys(filters).length) {
				const filtersBase64 = encodeURIComponent(Base64.encode(JSON.stringify(filters)));
				url = setUrlParameter(url, 'filter', filtersBase64);
			}
		}
		return url;
	},
	resultsPrepareRequest () {
		if (this.activeView !== 'list') {
			// Request searchParameters from timetable or timeline
			EventDispatcher.dispatch('showresult:requestResultUpdate', { target: this.activeView });
		} else {
			return this.resultsGetFirst();
		}
	},
	/**
	 * Resets searchParams.page to 0
	 * Saves searchParams.filters to localStorage
	 * Calls showresult_data.php
	 * Fades in and out the result container
	 * Shows no results message when appropriate
	 * @param {Object} searchParams
	 * @returns {Promise}
	 */
	async resultsGetFirst (searchParams = this.searchParamsForAPI()) {
		// Generate a UID for this search for use in the resultsGet function
		this.requestId = genUID();

		this?.displayMessage?.hide?.();

		// Save filters to URL
		this.saveFiltersToUrl();

		// Set search page to 0
		searchParams.page = 0;
		this.searchParams.page = 0;

		// Call API
		await this.resultsGet(searchParams, true);
		if (resultsFetcher.isError) {
			return;
		}

		// Show warning messages if applicable
		if (this.isYouthSearch && !resultsRender.response?.meta?.hasYouthTickets) {
			this.showNoYouthTicketsWarning();
			this.resultsPrepareRequest();
			return;
		} else if (!resultsRender?.response?.meta?.resultCount) {
			this.showResetFiltersMsg();
		}

		if (this.searchParams.initial) {
			// Render filters
			this.filters.set({ data: resultsRender.response, filtered: { ...this.searchParams.filters } });

			this.filters.renderFilterSummaries(this.searchParams.filters);
		}

		delete this.searchParams.initial;

		this.filters.renderFooter();
	},
	queueNextPageSpinner () {
		// Ideally we'd like to remove this line and have this be only function responsible for showing the spinner regardless of what view we are on
		if (this.activeView !== 'list') {
			return;
		}
		this.requestId = genUID();
		const currentRequestId = this.requestId;
	},
	/**
	 * Calls showresult_data.php and merges the response with the previous response
	 * Emits event showresult:resultUpdate for timeline and timetable
	 * Updates sorting tab prices
	 * Renders no more results
	 * @param {Object} searchParams
	 * @returns {Promise}
	 */
	resultsGet (searchParams, getFirst = false) {
		this.queueNextPageSpinner();
		if (getFirst) {
			this.elems.resultsContainer.classList.remove('with-results');
			this.elems.resultsContainer.classList.add('loading-results');
		}
		gebi('results-offline')?.remove?.();
		const currentRequestId = this.requestId;
		return resultsFetcher.get(searchParams).then((response) => {
			if (getFirst) {
				resultsRender.response.flights = [];
			}

			// If the request ID has changed then abort setting data and rendering
			if (currentRequestId !== this.requestId) {
				return;
			}
			// Sucessfull network request! Lets reset networkErrorCount
			this.networkErrorCount = 0;

			const responseMerged = {
				...resultsRender.response,
				...response,
				trips: {
					...resultsRender.response?.trips,
					...response?.trips
				},
				segments: {
					...resultsRender.response?.segments,
					...response?.segments
				},
				airportDetails: {
					...resultsRender.response?.airportDetails,
					...response?.airportDetails
				},
				flights: [
					...resultsRender.response?.flights || [],
					...response?.flights || []
				]
			};

			resultsRender.setData(responseMerged, searchParams);
			if (this.activeView === 'list') {
				// Render the new results
				resultsRender.render(response.flights, searchParams.page, this.elems.resultsContainer, 'list', {
					updateFeedback: this.searchParams.initial ? false : true
				});
			}

			// Render timeline or timetable
			this.emitResponseChanged(response.flights);

			// Render sorting tabs
			if (this.activeView !== 'timetable') {
				this.updateSortingTabs();
			}

			// Show no more results
			if (searchParams.page === resultsRender?.response?.meta?.numberOfPages) {
				this.renderNoMoreResults();
			}

			this.elems.resultsContainer.classList.remove('loading-results');
			this.elems.resultsContainer.classList.add('with-results');
		}).catch(this.resultsError.bind(this));
	},
	/**
	 *
	 * @returns
	 */
	async resultsGetNext (isForce = false) {
		const notListView = this.activeView !== 'list';
		const isLoading = resultsFetcher.isLoading;
		const isError = resultsFetcher.isError;

		if (!isForce && (notListView || isLoading || isError)) {
			return;
		}
		this.searchParams.page += 1;
		const response = await this.resultsGet(this.searchParamsForAPI());
		if (resultsFetcher.isError) {
			this.searchParams.page -= 1;
		}
		return response;
	},
	/**
	 *
	 * @param {Object} data
	 * @returns
	 */
	resultsError (data) {
		// don't display error message if the request was aborted
		if (data?.statusText === 'abort') {
			return;
		}

		this.elems.resultsContainer.classList.remove('loading-results');
		this.elems.resultsContainer.classList.add('with-results');

		// Update Timeline and Timetable views
		EventDispatcher.dispatch('showresult:requestResultUpdateError', { target: this.activeView });

		this.updateSortingTabs();

		// default to e9 if no message was passed, if backend replied with 'no data' then use error.searchTooOld
		let messageText = data?.error ? data.error : l('error.e9');
		if (data?.error === 'no data') {
			messageText = l('error.searchTooOld');
		}

		let displayMessage = { messageText: messageText };

		const _isNetworkError = isNetworkError(data);
		if (_isNetworkError) {
			// Set a fixed error message on network errors
			data.message = 'Failed to fetch: showresult_data.php\n' + data.message;
			if (this.activeView === 'list' && this.searchParams.page > 0 && resultsRender.response?.flights?.length) {
				this.offlineFooterRender();
			} else {
				displayMessage = { messageText: l('error.e14'), buttonText: l('global.simplePhrases.tryAgain'), callBack: () => window.location.reload() };
				this.showDisplayMessage(displayMessage);
				this.elems.resultsContainer.classList.remove('with-results');
				this.elems.resultsContainer.classList.add('loading-results');
			}
			if (isMobileView() && this.filters.html.classList.contains('filters--visible')) {
				this.filters.hide();
			}
			this.networkErrorCount += 1;
		} else {
			// Show deleted result list, invalid list error or network error message
			this.showDisplayMessage(displayMessage);
		}

		// This isn't a backend or a network issue but rather something that has gone wrong with the rendering logic so let's inform civet
		if (!('error' in data)) {
			Civet.reportError(data);
		}

		console.log('Error in resultsGet', data);
	},
	initFilters () {
		this.filters = new Filters({
			html: gebi('filters'),
			data: defaultFilterData,
			callbacks: {
				onUpdate: (filters) => {
					qsa('[data-filterkey="flexTicket"] input').forEach((input) => {
						input.checked = !!filters?.flexTicket;
					});
					// Unfortunately there is an infinite loop caused by the sliders without this line.
					if (JSON.stringify(filters) === JSON.stringify(this.searchParams.filters)) {
						return;
					}
					this.filters.renderFilterSummaries(filters);

					// Update local filters object (used to make network calls)
					this.searchParams.filters = filters;

					// Set the banners accordingly
					if (this.elems.flexTicketCheckbox) {
						this.elems.flexTicketCheckbox.checked = !!filters?.flexTicket;
					}

					// Set navbar checkbox
					if (this.elems.climateCheckbox) {
						this.elems.climateCheckbox.checked = !!filters?.climateCompensated;
					}

					// Set save filters state
					const isChecked = Object.keys(filters).filter(filterKey => !['alternativeHashes'].includes(filterKey)).length > 0;
					this.filters.toggleLoadFilters(isChecked);

					this.resultsPrepareRequest();


					// Update Price Calendar if available
					setTimeout(() => {
						this.priceCalendar?.update?.(this.searchParamsForAPI());
					}, 300);
				},
				onReset: () => {
					EventDispatcher.dispatch('showresult:filtersReset', this.activeView);

					// Reset all the banners
					qsa('.filterBanner input[type="checkbox"]').forEach((input) => {
						input.checked = false;
					});
				}
			}
		});
	},

	initWCAG () {
		qsa('.filtersView').forEach((filter) => {
			filter.querySelectorAll('a, button, label, input, textarea, select, .noUi-handle').forEach((elem) => {
				elem.tabIndex = -1;
			});

			if (isMobileView()) {
				filter.tabIndex = -1;
			}
		});

		gebi('ab-showresult-search-box').querySelectorAll('a, button, label, input, textarea, select, .noUi-handle, .abSearchBox__depart, .abSearchBox__return, .abSearchBox__passengers').forEach((elem) => {
			elem.tabIndex = -1;
		});

		if (isMobileView()) {
			qsa('.abSearchBox__cabin, .abShowResultSearchBox, #filters-column, .abBtn--resetFilters, .abBtn--hideFilters').forEach((elem) => {
				elem.tabIndex = -1;
			});
		}
	},

	initFilterBanner () {
		const filterBannerCheckboxes = qsa('.filterBanner__container');
		let hiddenFilterBannerCheckboxes = 0;

		// Filter banner
		// Adds events or hides any filters that have been preset on landing
		filterBannerCheckboxes.forEach((target) => {
			// If filter is preselected (landed with the url parameter set) then hide the checkbox and set the filter
			const { filterkey, filterselector } = target.dataset;
			if (this.searchParams.filters[filterkey]) {
				hiddenFilterBannerCheckboxes += 1;
				target.classList.add('pre-selected');
				qs('input', target).checked = true;
			} else {
				// add click listener to toggle the tooltip
				target.addEventListener('click', (event) => {
					event.stopPropagation();
					if (event.target.closest('.filterBanner__info')) {
						qs('.filterBanner__tooltip', target).classList.toggle('filterBanner__tooltip--visible');
						qs('.arrow-tooltip', target).classList.toggle('arrow-tooltip--visible');
					}
				});
			}

			target.addEventListener('change', () => {
				this.filters.html.querySelector(`input#${filterselector}`).click();
			});
		});


		this.filters.html.addEventListener('change', (event) => {
			const target = event.target;

			if (target.id == 'show-flex-ticket') {
				filterBannerCheckboxes.forEach(flexTicketBanner => (flexTicketBanner.querySelector('input').checked = target.checked));
			}
			if (target.id == 'show-climate-compensation') {
				this.setCarbonOffsetTabState(target.checked);
			}
		});


		// If all the filter banner checkboxes were hidden then hide the parent containers
		// Else bind events
		if (filterBannerCheckboxes.length === hiddenFilterBannerCheckboxes) {
			qsa('.filterBanner').forEach((element) => {
				element.classList.add('pre-selected');
			});
		} else {
			document.addEventListener('click', (event) => {
				const elem = event.target;

				if (!elem.closest('.filterBanner__tooltip') && !elem.closest('.filterBanner__info')) {
					filterBannerCheckboxes.map((target) => {
						qs('.filterBanner__tooltip', target).classList.remove('filterBanner__tooltip--visible');
						qs('.arrow-tooltip', target).classList.remove('arrow-tooltip--visible');
					});
				}
			});
		}
	},

	initFavorite () {
		// show favs number on load
		if (!this.favorite) {
			this.favorite = new Favorite();
		}

		setTimeout(() => {
			this.updateFavBtns();
		}, 1000);
	},

	updateFavBtns () {
		const goToFavBtns = qsa('.toFavorite__btn');

		if (this.favorite.favouriteArray.length > 0) {
			goToFavBtns.forEach((favBtn) => {
				favBtn.dataset.amount = this.favorite.favouriteArray.length;
				favBtn.dataset.fill = true;
			});
		} else {
			goToFavBtns.forEach((favBtn) => {
				delete favBtn.removeAttribute('data-amount');
				delete favBtn.removeAttribute('data-fill');
			});
		}
	},

	kalleBakomShow (index, resultIsPriceCalendar = false) {
		const isListOrTimetable = ['list', 'timetable'].includes(this.activeView);
		const isReturnTrip = resultsRender.response?.meta?.tripType === 1;
		const isMultiCitySearch = resultsRender.response?.meta?.tripType === 2;

		if (!isListOrTimetable || resultIsPriceCalendar || isMultiCitySearch) {
			return;
		}

		const trip = resultsRender.getTripsAndSegmentsForFlight(resultsRender.response?.flights?.[index]);
		const outboundSegments = trip?.[0]?.segments;
		if (!outboundSegments) {
			return;
		}

		const { departureAirport } = outboundSegments[0];
		const { arrivalAirport, arrivalDate: pickupDateTime } = last(outboundSegments);

		const inboundSegments = last(trip).segments[0];
		const { departureDate: returnDateTime } = inboundSegments;

		if (!this.kalleBakom) {
			this.kalleBakom = KalleBakom();
		}

		window.kalleTripTrack = { searchBox: window.searchBox?.data, trip: trip };

		this.kalleBakom.display({
			airportInformation: resultsRender.getAirport(arrivalAirport),
			kallebakomRequest: [arrivalAirport, departureAirport, isReturnTrip, pickupDateTime, returnDateTime, js_params.passengers, js_params?.childrenAges || [], js_params?.youthAges || []]
		});
	},

	/**
	 *
	 * @returns {Boolean}
	 */
	isValidSearch () {
		const { searchDateIsOld, searchIsDeleted, isupdating } = js_params;

		const searchFailed = isupdating === 2;

		if (searchDateIsOld || searchIsDeleted || searchFailed) {
			const config = {
				style: 'showResult',
				buttonText: l('global.simplePhrases.back'),
				callBack: () => (location.href = '/')
			};

			if (searchFailed) {
				config.messageText = l('error.e8');
			} else if (searchIsDeleted) {
				config.messageText = l('showresult.message.deletedWarning');
			} else if (searchDateIsOld) {
				config.messageText = l('error.searchTooOld');
				config.buttonText = l('searchBox.search.searchBtnText');
				config.callBack = () => this.showSearchBoxExtended();
			}

			this.showDisplayMessage(config);

			return false;
		}

		return true;
	},
	initMain () {
		// Hackish youthSearch filter initialisation
		if (this.isYouthSearch) {
			this.searchParams.filters.youthTicket = 1;
		}

		this.setCarbonOffsetTabState(!!this.searchParams?.filters?.climateCompensated);
	},
	getSavedFilters () {
		const savedFilters = storedItemRead('savedFilters') || {};

		// Remove arrival time, departure time and airport stops from saved filters because
		// they may not be relevant to this search result
		delete savedFilters.arrivalTime;
		delete savedFilters.departureTime;
		delete savedFilters.airportStops;
		delete savedFilters.traveltime;

		// Merge URL filters and filters from localStorage
		const mergedFilters = { ...savedFilters, ...this.searchParams.filters };


		storedItemCreate('savedFilters', mergedFilters);
	},
	initDateSelector () {
		// setup dateSelector for Price Calendar & Price Cutter
		const returnTrip = js_params.returntrip;
		const html = gebi('ab-showresult-date-selector');
		const header = qs('.abDateSelector__header', html);
		const title = qs('.abDateSelector__title', header);
		const iataFrom = js_params.from.iata;
		const iataTo = js_params.to.iata;
		const outboundDate = dateFormat(new Date(js_params.leavedate), 'd MMM');
		const inboundDate = returnTrip
			? dateFormat(new Date(js_params.homedate), 'd MMM')
			: null;
		const headerSpan = document.createElement('SPAN');

		headerSpan.innerHTML = `
			${this.isAllNumeric(iataFrom) ? this.truncateString(js_params.from.city, 5) : iataFrom} <span class="abDateSelector__researchArrows ${(returnTrip) ? 'icon-switch' : 'icon-arrow-right'}"></span> ${this.isAllNumeric(iataTo) ? this.truncateString(js_params.to.city, 5) : iataTo}, ${outboundDate}
		`;

		if (returnTrip) {
			headerSpan.innerHTML += ` - ${inboundDate}`;
		}

		title.appendChild(headerSpan);

		this.dateSelector = new DateSelector({
			viewType: 'pricecalendar',
			html: html,
			callbacks: {
				onShow: () => {
					// Change parent state
					classList('.abBtnCard--container').add('active');
					// Change child state
					classList('.abBtnCard.active').remove('active');
					classList(`.abBtnCard[data-show="${this.dateSelector.viewType}"]`).add('active');

					html.style.zIndex = this.isMobile ? 3 : 'auto';
					html.style.position = this.isMobile ? 'fixed' : 'relative';

					header.style.marginTop = 0;
				},
				onHide: () => {
					this.heroArrowBounceStop();
					this.priceCalendar?.close?.();

					if (window.openFeature) {
						logEvent(events['close' + window.openFeature], eventCategories.feature);
					}

					window.openFeature = null;

					// Change parent state
					classList('.abBtnCard--container.active').remove('active');
					// Change child state
					classList('.abBtnCard.active').remove('active');

					html.style.zIndex = '';
					html.style.position = '';

					header.style.marginTop = '';
				},
				onViewChange: (viewType) => {
					// Change child state
					classList('.abBtnCard.active').remove('active');
					classList(`.abBtnCard[data-show="${viewType}"]`).add('active');
					if (viewType === 'pricecutter' && this.priceCutter) {
						this.priceCutter.load();
						this.priceCalendar.close();
						logEvent(events.openPriceCutter, eventCategories.feature);
						if (window.openFeature) {
							logEvent(events['close' + window.openFeature], eventCategories.feature);
						}
						window.openFeature = 'PriceCutter';
					} else if (viewType === 'pricecalendar') {
						if (!this.priceCalendar) {
							EventDispatcher.addEventListener('priceCalendar:loaded', () => {
								this.priceCalendar?.open?.();
							});
						} else {
							this.priceCalendar?.open?.();
						}
						logEvent(events.openPriceCalendar, eventCategories.feature);
						if (window.openFeature) {
							logEvent(events['close' + window.openFeature], eventCategories.feature);
						}
						window.openFeature = 'PriceCalendar';
					}
				}
			}
		});
	},
	initPriceCutter () {
		if (!js_params?.arePriceMatricesEnabled) {
			return;
		}
		if (js_params?.disablePriceCutter === true) {
			document.querySelector('.abBtnCard--priceCutter').classList.add('disabled');
			document.querySelector('.abTabNav__btn--pricecutter').classList.add('disabled');
			return;
		}
		const searchData = {
			fromIata: js_params.from.iata,
			toIata: js_params.to.iata,
			departDate: js_params.leavedate,
			returnDate: js_params.homedate,
			returnTrip: js_params.returntrip,
			adults: js_params.adults,
			childrenAges: js_params.childrenAges,
			price: js_params.price
		};

		this.priceCutter = new PriceCutter({ searchData, dateSelector: this.dateSelector });
	},
	initPriceCalendar () {
		// return;
		if (!js_params.arePriceMatricesEnabled || !js_params?.module?.PriceCalendar?.searchData) {
			return;
		}
		const config = JSON.parse(js_params?.module?.PriceCalendar?.searchData);

		const savedSearch = storedItemRead('userSavedSearch');
		const searchNearby = urlParameterGet('nearbyDates');
		config.nearbyDates = (
			parseInt(savedSearch?.nearbyDates, 10) === 1
			&& parseInt(searchNearby, 10) === 1
		);

		this.priceCalendar = new PriceCalendar(gebi('ab-price-calendar'), config);
		EventDispatcher.dispatch('priceCalendar:loaded');
	},
	initPriceWatcher () {
		this.priceWatcher = PriceWatcher.init();
	},
	initHero () {
		this.heroSetHeight();
	},
	heroAnimate () {
		const height = this.heroGetMinHeight();
		const style = 'priceCalendarHero--animated';

		this.elems.hero.style.minHeight = 0;
		this.elems.hero.classList.add(style);

		setTimeout(() => {
			this.elems.hero.style.minHeight = height + 'px';
			this.elems.hero.classList.remove(style);
		}, 3200);
	},
	heroGetMinHeight () {
		return Math.max(this.elems.hero.offsetHeight, qs('.priceCalendarHero__nav', this.elems.hero).clientHeight);
	},
	heroSetHeight () {
		this.elems.hero.style.minHeight = this.heroGetMinHeight() + 'px';
	},
	heroArrowBounce () {
		this.elems.hero.classList.add('priceCalendarHero--bounce');
	},
	heroArrowBounceStop () {
		this.elems.hero.classList.remove('priceCalendarHero--bounce');
	},
	initSearchBox () {
		const urlFilters = getUrlVarBase64('filter');

		let nearbyDates = 0;
		let direct = (urlFilters?.numStops === 0) || 0;
		let baggage = urlFilters?.baggageIncluded || 0;
		let cabinBaggage = urlFilters?.cabinBaggageIncluded || 0;
		let climateComp = urlFilters?.climateCompensated || 0;
		let flexTicket = urlFilters?.flexTicket || 0;

		const userSavedSearch = storedItemRead('userSavedSearch');

		if (userSavedSearch) {
			direct = parseInt(userSavedSearch.direct, 10);
			baggage = parseInt(userSavedSearch.baggage, 10);
			cabinBaggage = parseInt(userSavedSearch.cabinBaggage, 10);
			nearbyDates = parseInt(userSavedSearch.nearbyDates, 10);
			climateComp = parseInt(userSavedSearch.climateComp, 10);
			flexTicket = parseInt(userSavedSearch.flexTicket, 10);
		}

		const searchboxConfig = {
			direct: direct,
			baggage: baggage,
			cabinBaggage: cabinBaggage,
			nearbyDates: nearbyDates,
			climateComp: climateComp,
			flexTicket: flexTicket
		};

		// If the result exists, add legs, cabin ( => ticket class) and trip type
		// Else, let SearchBox module fall back to default
		if (js_params.id !== '') {
			const cabin = parseInt(js_params.classType, 10);
			const legs = JSON.parse(JSON.stringify(js_params.showresultSearchParams));

			searchboxConfig.legs = legs;
			searchboxConfig.cabin = cabin;
			searchboxConfig.tripType = js_params.returntrip;
		}

		// Format passenger data
		const selectedPassengersView = this.isYouthSearch ? 'youth' : 'default';
		if (selectedPassengersView === 'default') {
			searchboxConfig.passengers = {
				adults: js_params.adults,
				child: {
					count: js_params.childrenAges.length,
					ages: js_params.childrenAges
				}
			};
		} else {
			searchboxConfig.passengers = {
				youth: {
					count: js_params?.youthAges?.length || js_params.passengers,
					ages: js_params?.youthAges || []
				}
			};
		}

		this.searchBox = new SearchBox({
			id: 'abSearchBox',
			dateSelector: gebi('ab-showresult-dateselector-search-box'),
			showresultMode: true,
			selectedPassengersView: selectedPassengersView,
			config: searchboxConfig,
			callbacks: {
				// This function can be called in two cases:
				// 1. On initial page load (showresult)
				// 2. When the user clicks the search button
				onPreviewReceived: (instance, previewGroup) => {
					if (this.searchParams.initial) {
						if (previewGroup?.flights?.length) {
							this.setupPreviewResultsTimeout(500);
							this.generateTempResult(previewGroup);
						}
					} else {
						instance.cancelSearch();

						instance.showresultURL = this.setPersisentFiltersToUrl(instance.showresultURL);

						location.href = instance.showresultURL;
					}
				},
				onSearchComplete: async (instance) => {
					/**
					 * This is to prevent a specific bug where there is only one OTA replying but they reply last.
					 * So we go from status searching to complete with no preview in-between.
					 */
					const { id } = getUrlVars(instance.showresultURL);

					// This function will check if the legs are the same, if the legs have not changed we will append the filters to the url
					instance.showresultURL = this.setPersisentFiltersToUrl(instance.showresultURL);

					if (instance.data.forceNearbyDates) {
						instance.showresultURL = setUrlParameter(instance.showresultURL, 'forceNearbyDates', 1);
					}

					if (!instance.resultCount || this.searchParams.id !== id) {
						location.href = instance.showresultURL;
						return;
					}

					document.body.classList.remove('search-loading');

					this.heroSetHeight();
					this.clearPreviewResultsInterval();
					await this.resultsPrepareRequest();

					// If nearby dates is selected or we haven't shown the animation yet, show it
					if (nearbyDates || !storedItemRead('animatePriceHunterHero')) {
						storedItemCreate('animatePriceHunterHero', 1);

						setTimeout(() => this.heroAnimate(), 400);
					}
				},
				onSearchError: (instance) => {
					// if nearbyDates is not set then show a modal asking the user if they want to search nearby dates
					if (!nearbyDates) {
						location.href = `${instance.showresultURL}&showNearbyDatesModal=1`;
					} else {
						document.body.classList.remove('search-loading');
					}
				}
			}
		});


		if (nearbyDates) {
			this.heroArrowBounce();
		}

		this.setHeaderFromToDates(this.searchBox.data.tripType, this.searchBox.data.legs);
	},
	initEvents () {
		const { header, hero, footer, scrollToTop, searchBoxWrapper } = this.elems;
		const searchBoxInner = qs('.abShowResultSearchBox__inner', searchBoxWrapper);
		const heroHead = qs('.priceCalendarHero__head', hero);
		const heroNav = qs('.priceCalendarHero__nav', hero);
		const heroTabNav = qs('.abTabNav', heroNav);
		const loadHeight = window.innerHeight / 2;

		let defaultHeroHeight = this.elems.hero.offsetHeight;
		let startTouchPos = 0;
		let stopYPos = 0;
		let moveYDist = 0;
		let previousYPos = 0;
		let direction = 1;
		let prevDirection = 1;
		let totalScrollDistance = 0;
		const footerThreshold = 10;
		let isSortingFooterVisible = this.isMobile && this.elems.footer.style.bottom == 0;

		let resizeTimer;
		let scrollTimer;

		const positionCookieNotice = () => {
			if (!window.Main?.cookieNotice?.html) {
				return;
			}
			const footerHeight = this.elems.footer.offsetHeight;
			window.Main.cookieNotice.html.style.bottom = isSortingFooterVisible
				? footerHeight + 'px'
				: '';
		};

		positionCookieNotice();

		const refreshInterface = () => {
			const headerHeight = header.children[0].offsetHeight;
			const footerHeight = footer.offsetHeight;
			const scrollToTopButtonThreshold = window.innerHeight * 2;
			const currrentYPos = window.pageYOffset;
			const top = currrentYPos + headerHeight;
			const distYScroll = Math.abs(currrentYPos - previousYPos);
			prevDirection = direction;
			direction = currrentYPos > previousYPos ? 'down' : 'up';
			// set hero + sorting bar styles
			if (!resultsRender.isMultiCitySearch) {
				const isScrolledPastHeader = top >= 121;
				heroNav.classList.toggle('priceCalendarHero__nav--fixed', isScrolledPastHeader);
				heroTabNav.classList.toggle('abTabNav--sortResultAlt', isScrolledPastHeader);
			}

			// show/hide sorting footer on scroll
			if (this.isMobile) {
				totalScrollDistance = prevDirection === direction ? totalScrollDistance + distYScroll : distYScroll;
				const isFooterThresholdReached = totalScrollDistance > footerThreshold;

				const drawer = gebi('drawer');

				if ((direction === 'up' && isFooterThresholdReached) || previousYPos < 10) {
					footer.style.bottom = '';
					if (drawer) {
						drawer.style.bottom = '5rem';
					}
					isSortingFooterVisible = true;
				} else if (direction === 'down' && isFooterThresholdReached) {
					footer.style.bottom = `-${footerHeight + 10}px`;
					if (drawer) {
						drawer.style.bottom = '0';
					}
					isSortingFooterVisible = false;
				}

				positionCookieNotice();
			}

			// show/hide scrollToTop button
			if (direction === 'down' && currrentYPos > scrollToTopButtonThreshold) {
				if (footer.style.bottom || !this.isMobile) {
					scrollToTop.classList.add('abScrollToTop--visible');
				}
			} else {
				scrollToTop.classList.remove('abScrollToTop--visible');
			}

			previousYPos = currrentYPos;
		};

		window.addEventListener('resize', (event) => {
			if (resizeTimer) {
				cancelAnimationFrame(resizeTimer);
			}

			resizeTimer = requestAnimationFrame(() => {
				this.heroSetHeight();

				const dateSelectorVisible = this.dateSelector?.html.classList.contains('abDateSelector--visible');

				this.isMobile = isMobileView();

				if (this.isMobile) {
					this.elems.header.classList.add('sticky');

					if (dateSelectorVisible) {
						this.dateSelector.html.style.zIndex = 2;
						this.dateSelector.html.style.position = 'fixed';
					}
				} else {
					this.elems.header.classList.remove('sticky');

					if (dateSelectorVisible) {
						this.dateSelector.html.style.zIndex = 'auto';
						this.dateSelector.html.style.position = 'relative';
					}
				}

				refreshInterface();
			});
		});

		window.addEventListener('scroll', (event) => {
			if (scrollTimer) {
				cancelAnimationFrame(scrollTimer);
			}

			scrollTimer = requestAnimationFrame(() => {
				if (isFullScreen()) {
					return;
				}

				refreshInterface();
			});
		});

		document.addEventListener('click', (event) => {
			const elem = event.target;

			// Add to favorites
			if (elem.closest('.abResult__flightFavBtn')) {
				event.stopPropagation();

				const addFavorite = () => {
					const flightIndex = elem.closest('[data-index]').getAttribute('data-index');
					const flight = resultsRender.getFlightRenderData(resultsRender.response.flights[flightIndex], flightIndex, 'list');

					if (elem.closest('.abResult__flightFavBtn').classList.contains('favorited')) {
						elem.closest('.abResult__flightFavBtn').classList.remove('favorited');
						const favouriteUid = this.favorite.itemGenerateUid(js_params.id, flight.orgIndex, flight.agent.urlOptions);
						const favouriteIndex = this.favorite.itemGetIndexByUid(favouriteUid);
						this.favorite.itemRemoveByIndex(favouriteIndex, false);
						logEvent(events.removeFavourite, eventCategories.feature, flight.hashes);
					} else {
						elem.closest('.abResult__flightFavBtn').classList.add('favorited');
						this.favorite.itemCreate(
							js_params.id,
							flight.orgIndex,
							this.searchParams.filters,
							flight.hashes,
							flight.agent.urlOptions
						);
						logEvent(events.addFavourite, eventCategories.feature, flight.hashes);
					}

					this.updateFavBtns();
				};

				if (window?.Main?.cookieNotice && !window?.Main?.cookieNotice?.isFunctionalCookiesSet) {
					EventDispatcher.dispatch('cookieNotice:blockingModal:show');
					EventDispatcher.addEventListener('cookieNotice:blockingModal:accept', () => {
						addFavorite();
					});
					return;
				} else {
					addFavorite();
				}

				return;
			}

			if (elem.classList.contains('otaLink') && js_params.searchDateIsTooOld) {
				event.preventDefault();
				return;
			}

			if (js_params.kalleBakomEnabled && elem.classList.contains('otaLink')) {
				const resultIsPriceCalendar = elem.closest('#priceCalendarResultModal');

				this.kalleBakomShow(elem.closest('.abResult').dataset.index, resultIsPriceCalendar);

				return;
			}

			// Search box hide
			if (elem.closest('[data-action="hide-search-box"]') || (elem.classList.contains('abShowResultSearchBox__inner') && isMobileView())) {
				// EventDispatcher.dispatch('searchBoxReset');
				this.hideSearchBoxExtended(200);
				return;
			}

			// Search box show
			if (elem.closest('[data-action="show-search-box"]')) {
				this.showSearchBoxExtended();
			}

			// Enable toggle mode
			if (elem.closest('[data-action="goToFav"]')) {
				if (this.favorite.favouriteArray.length) {
					window.location = this.favorite.pageUrl;
				} else {
					toaster.newToast(l('fav.ui.noFavs'), 'info', 'stays');
				}
			}


			// List / Timetable / Timeline toggle
			if (elem.closest('[data-action="change-view"]')) {
				if (elem.classList.contains('abTabNav__btn--active')) {
					return;
				}
				this.displayMessage?.hide?.();

				this.activeView = elem.dataset.type;

				qsa('.abTabNav--changeView').forEach((menu) => {
					[...menu.children].forEach((btn) => {
						btn.classList.toggle('abTabNav__btn--active', btn.dataset.type === this.activeView);
					});
				});

				if (this.activeView === 'list') {
					this.resultsGetFirst();
					this.elems.resultsContainer.classList.add('with-results');
					this.elems.resultsContainer.classList.remove('loading-results');
				} else {
					this.elems.resultsContainer.classList.add('loading-results');
					this.elems.resultsContainer.classList.remove('with-results');
				}

				EventDispatcher.dispatch('showresult:setActiveView', this.activeView);

				if (this.activeView === 'list') {
					logEvent(events.changedisplayList, eventCategories.changeDisplay);
				} else if (this.activeView === 'timetable') {
					logEvent(events.changedisplayTimetable, eventCategories.changeDisplay);
				} else {
					logEvent(events.changedisplayTimeline, eventCategories.changeDisplay);
				}

				return;
			}

			// Price calendar / Price cutter toggle
			if (elem.closest('.abBtnCard')) {
				const { searchDateIsOld, searchIsDeleted } = js_params;

				if (searchDateIsOld || searchIsDeleted) {
					return;
				}

				const btn = elem.closest('.abBtnCard');
				const btns = btn.parentElement.children;
				const type = btn.dataset.show;

				for (let i = 0; i < btns.length; i++) {
					btns[i].classList.remove('active');
				}

				if (this.dateSelector.viewType === type && this.dateSelector.open) {
					this.dateSelector.hide();
				} else if (this.dateSelector.viewType !== type || !this.dateSelector.open) {
					this.dateSelector.toggleViewType(type);
					this.dateSelector.show();
				}

				// if (this.priceCalendar && type === 'pricecalendar') {
				// 	this.priceCalendar.open();
				// }
			}

			// Price watcher show
			if (elem.closest('[data-action="show-price-guide"]')) {
				if (!this.priceWatcher.showresultInitialised) {
					this.priceWatcher.showresultInitialised = true;
					this.priceWatcher.populateForm({
						iata_from: js_params.from.iata,
						city_from: js_params.from.city,
						iata_to: js_params.to.iata,
						city_to: js_params.to.city,
						price: js_params.price,
						adults: js_params.adults,
						children: js_params.childrenAges.filter(age => age > 0).length,
						infants: js_params.childrenAges.filter(age => age == 0).length,
						date_from: js_params.leavedate,
						date_to: js_params.homedate
					});
				}
				if (qs('#priceGuideWrapper').dataset.slidDown) {
					if (window.scrollY > 200 && isMobileView()) {
						window.scrollTo({ top: 0 });
					} else {
						logEvent(events.closePriceAlert, eventCategories.feature);
						slideToggle('#priceGuideWrapper');
					}
				} else {
					logEvent(events.openPriceAlert, eventCategories.feature);
					slideToggle('#priceGuideWrapper');
					window.scrollTo({ top: 0 });
				}

				return;
			}

			// Filters show
			if (elem.closest('[data-action="show-filters"]')) {
				this.filters.show();
				return;
			}

			// Filters hide
			if (elem.closest('[data-action="hide-filters"]')) {
				this.filters.hide();
				return;
			}

			// Sorting tabs
			if (elem.closest('.abTabNav--sortResult .sort-type')) {
				const clickedSortingType = elem.dataset.sort;

				if (!this.isBestSortingInfoRead) {
					if (clickedSortingType === 'bestValue') {
						showElement('#bestSortingInfo');
					} else if (js_params.domain !== 'uberflieger.de') {
						hideElement('#bestSortingInfo');
					}
				} else {
					hideElement('#bestSortingInfo');
					qs('#bestSortingInfo').classList.add('gone');
					qs('.abTabNav--sortResultLarge').classList.add('no-alert');
				}

				if (clickedSortingType === 'carbonOffset') {
					qs('input#show-climate-compensation', this.filters.html).click();
					return;
				}
				this.updateSorting(clickedSortingType);
				return;
			}

			// Price calendar sorting radio buttons
			if (elem.closest('.priceCalendarSorting input[type=radio]')) {
				this.updateSorting(elem.value);
				return;
			}

			// Scroll to top button
			if (elem.closest('#ab-scroll-to-top')) {
				event.stopPropagation();

				const scrollToTop = elem.closest('#ab-scroll-to-top');

				scrollToTop.classList.remove('abScrollToTop--visible');

				window.scrollTo({ top: 0, behavior: 'smooth' });

				return;
			}

			// Price calendar drop down (mobile only)
			if (elem.closest('#ab-price-calendar-hero') && !elem.closest('.filterBanner')) {
				event.stopImmediatePropagation();

				if (this.elems.hero.classList.contains('priceCalendarHero--animated') || this.elems.hero.classList.contains('priceCalendarHero--disabled')) {
					return;
				}

				if (resultsRender?.response?.flights?.length) {
					this.showDateSelector();
				}

				return;
			}

			// Tabs in kallebakom
			if (elem.hasAttribute('data-kalletab')) {
				if (!elem.classList.contains('abTabNav__btn--active')) {
					const oldAtribute = qs('.abTabNav__btn--active[data-kalletab]').getAttribute('data-kalletab');

					// Toggle visible option
					classList('.abTabNav__btn--active[data-kalletab]').remove('abTabNav__btn--active');
					classList('.active[data-kalleview]').remove('active');
					elem.classList.add('abTabNav__btn--active');

					gebi('kallebakomModal').classList.remove(oldAtribute);

					const newTab = '[data-kalleview="' + elem.getAttribute('data-kalletab') + '"]';
					classList(newTab).add('active');
					gebi('kallebakomModal').classList.add(elem.getAttribute('data-kalletab'));

					// Change url and text of modal button
					const newUrl = qs(newTab + ' .kallebakom_cta').getAttribute('data-url');
					const newKalleid = qs(newTab + ' .kallebakom_cta').getAttribute('data-kalleid');
					const newText = qs(newTab + ' .kallebakom_cta').innerHTML;
					qs('.kallebakom .kallebakom_maincta').setAttribute('data-url', newUrl);
					qs('.kallebakom .kallebakom_maincta').setAttribute('data-kalleid', newKalleid);
					qs('.kallebakom .kallebakom_maincta').innerHTML = newText;
				}
			}

			// Old search result warning
			if (elem.closest('#closeBestSortingInfo')) {
				event.stopPropagation();

				qs('#bestSortingInfo').classList.add('gone');
				qs('.abTabNav--sortResultLarge').classList.add('no-best-alert');

				this.isBestSortingInfoRead = 1;

				logEvent(events.readmoreBestSorting, eventCategories.readmore, this.isBestSortingInfoRead);

				return false;
			}

			if (elem.closest('#bestSortingInfo')) {
				event.stopPropagation();

				const warningDialog = new TooltipDialog(l('showresult.dialogTooltip.bestFunctionTitle'), l('showresult.dialogTooltip.bestFunctionText'), () => { }, { singleButton: true }, [], elem.closest('#bestSortingInfo').dataset.readmore);

				warningDialog.show();
			}


			if (elem.closest('[data-action="copyDiscount')) {
				copyToClipboard(elem.closest('.discountBox').querySelector('input').value).then(() => {
					toaster.newToast(l('kallebakom.forex.clipboard'), 'info', 'top');
				});
			}

			// Old search result warning "update prices" link
			if (elem.id === 'update-current-prices') {
				event.preventDefault();

				logEvent(events.refreshsearch, eventCategories.search, eventCategories.search);

				qs('#ab-showresult-search-box button[data-action="search"]').click();
			}
		},
		false
		);

		document.addEventListener('touchstart', (event) => {
			const elem = event.target;

			if (elem.closest('.abSearchBox__menu')) {
				return;
			}

			if (elem.closest('#ab-price-calendar-hero')) {
				if (
					heroNav.classList.contains('priceCalendarHero__nav--fixed')
					|| this.elems.hero.classList.contains('priceCalendarHero--animated')
					|| this.elems.hero.classList.contains('priceCalendarHero--disabled')
				) {
					return;
				}

				if (window.scrollY === 0) {
					startTouchPos = event.touches[0].clientY;
				}

				defaultHeroHeight = this.elems.hero.offsetHeight;

				return;
			}

			if (elem.closest('.abShowResultSearchBox__inner')) {
				if (this.allowSearchBoxTouchMove()) {
					searchBoxInner.classList.add('abShowResultSearchBox__inner--touch');
					startTouchPos = event.touches[0].clientY;
				}
			}
		},
		{ passive: false }
		);

		document.addEventListener('touchmove', (event) => {
			const elem = event.target;

			if (this.searchBox.html.classList.contains('abSearchBox--show-overlay')) {
				return;
			}

			if (elem.closest('.abHeader')) {
				if (typeof event.cancelable !== 'boolean' || event.cancelable) {
					event.preventDefault();
				}

				return;
			}

			if (elem.closest('#ab-price-calendar-hero')) {
				if (typeof event.cancelable !== 'boolean' || event.cancelable) {
					event.preventDefault();
				}

				if (heroNav.classList.contains('priceCalendarHero__nav--fixed')) {
					return;
				}
				if (this.elems.hero.classList.contains('priceCalendarHero--animated') || this.elems.hero.classList.contains('priceCalendarHero--disabled')) {
					return;
				}

				const distance = Math.floor(event.touches[0].clientY - startTouchPos);
				const calcHeight = Math.floor(defaultHeroHeight + distance);
				const calcOpacity = 1 - distance / loadHeight;

				if (distance > 0) {
					this.elems.hero.style.height = calcHeight + 'px';
					heroHead.style.opacity = calcOpacity;
					heroNav.style.opacity = calcOpacity;
				}

				return;
			}

			if (elem.closest('.abShowResultSearchBox__inner') || elem.closest('.abShowResultSearchBox__overlay')) {
				if (this.allowSearchBoxTouchMove()) {
					event.stopPropagation();

					const distance = event.touches[0].clientY - startTouchPos;
					const height = searchBoxInner.offsetHeight - this.elems.searchBoxWrapper.scrollTop;

					if (distance < 0) {
						if (height <= window.innerHeight) {
							if (typeof event.cancelable !== 'boolean' || event.cancelable) {
								event.preventDefault();
							}

							event.stopPropagation();

							stopYPos = event.touches[0].clientY;
							moveYDist = stopYPos - startTouchPos;

							searchBoxInner.style.transform = `translate3d(0, ${distance}px, 0)`;
						}
					}
				}

				return;
			}

			if (elem.closest('.abShowResultSearchBox__overlay')) {
				if (typeof event.cancelable !== 'boolean' || event.cancelable) {
					event.preventDefault();
				}
			}
		},
		{ passive: false }
		);

		document.addEventListener('touchend', (event) => {
			const elem = event.target;

			if (elem.closest('#ab-price-calendar-hero')) {
				if (this.elems.hero.classList.contains('priceCalendarHero--animated') || this.elems.hero.classList.contains('priceCalendarHero--disabled')) {
					return;
				}

				const height = this.elems.hero.offsetHeight;

				if (height > defaultHeroHeight) {
					if (height >= loadHeight && resultsRender?.response?.flights?.length) {
						this.showDateSelector();
					}

					startTouchPos = 0;

					this.elems.hero.style.height = '';
					heroHead.style.opacity = '';
					heroNav.style.opacity = '';

					footer.style.bottom = 0;
					footer.classList.remove('hidden');
				}

				return;
			}

			if (elem.closest('.abShowResultSearchBox__inner')) {
				event.stopPropagation();

				if (this.allowSearchBoxTouchMove()) {
					if (moveYDist < -120) {
						startTouchPos = 0;
						stopYPos = 0;
						moveYDist = 0;

						searchBoxInner.classList.remove('abShowResultSearchBox__inner--touch');
						searchBoxInner.style.transform = 'translate3d(0, -100%, 0)';

						setTimeout(() => {
							searchBoxWrapper.classList.remove('abShowResultSearchBox--visible');
							document.body.classList.remove('no-scroll');
							searchBoxInner.removeAttribute('style');
							document.body.style.position = '';
						}, 400);
					} else {
						searchBoxInner.classList.remove('abShowResultSearchBox__inner--touch');
						searchBoxInner.removeAttribute('style');
					}
				}
			}
		});

		// Special keyboard situations for show result page
		document.body.addEventListener('keyup', (event) => {
			if (event.key === 'Enter' || event.key === 'Space') {
				if (event.target.classList.contains('abResult')) {
					if (isMobileView()) {
						event.target.click();
						tabTrappingHelper(gebi('resultModal'), 'Result', () => qs('.js-close-modal').click());
					} else {
						if (!event.target.classList.contains('abResult--expanded')) {
							event.target.click();
							tabTrappingHelper(event.target, 'Result', () => event.target.click(), false, false);
							return;
						}
					}
				}

				if (event.target.classList.contains('sort-type')) {
					event.target.click();
					return;
				}

				if (event.target.classList.contains('abToggleSwitch')) {
					event.target.click();
					return;
				}

				if (isMobileView()) {
					if (event.target.classList.contains('abHeader__btn--searchData')) {
						const container = gebi('abSearchBox');
						tabTrappingHelper(container, 'Searchbox', () => this.hideSearchBoxExtended(), false, qsa('.abSearchBox__navBtn, .SearchFieldMulti__field, .abSearchBox__depart, .abSearchBox__return, .abSearchBox__passengers, .abSearchBox__cabin, .abSearchBox__view, .abSearchBox__checkbox > .checkbox, .abToggleSwitch, .abSearchBox__searchBtn', container), false);
					}

					if (event.target.classList.contains('abBtn--showFilters')) {
						// General filter button
						event.target.click();
						const container = gebi('filterMobileNav');
						tabTrappingHelper(container, 'Filter-list', () => this.filters.hide(), false, qsa('.filtersNavBtn, .abBtn--hideFilters', container));
					}

					if (event.target.classList.contains('filtersNavBtn')) {
						// Filter left navigation
						event.target.click();
						const container = gebi('filter-' + event.target.dataset.show + '-section');
						container.tabIndex = 0;
						tabTrappingHelper(container, 'Filter-' + event.target.dataset.show, () => this.filters.hide());
					}
				} else {
					if (event.target.classList.contains('abShowResultSearchBox')) {
						const container = gebi('abSearchBox');

						if (!searchBoxWrapper.classList.contains('abShowResultSearchBox--visible')) {
							this.showSearchBoxExtended();
						}

						tabTrappingHelper(container, 'Searchbox', () => this.hideSearchBoxExtended(), false, qsa('.SearchFieldMulti__field, .abSearchBox__depart, .abSearchBox__return, .abSearchBox__passengers, .abSearchBox__cabin, .abSearchBox__view, .abSearchBox__checkbox > .checkbox, .abSearchBox__searchBtn', container), false);
						return;
					}

					if (event.target.classList.contains('filters-column')) {
						const container = gebi('filters-column');
						tabTrappingHelper(container, 'Filter-list', false, false, qsa('.filtersView', container));
						return;
					}

					if (event.target.classList.contains('filtersView')) {
						tabTrappingHelper(event.target, event.target.ariaLabel?.replace(/\s+/g, '-').toLowerCase(), false, false, qsa('a, button, input:not([type="checkbox"]), .checkbox, textarea, select, .noUi-handle, .abToggleSwitch', event.target));
					}
				}
			}
		});

		EventDispatcher.addEventListener('searchBox.searchError', (error) => {
			this.clearPreviewResultsInterval();
			hideElement(this.elems.resultsContainer);
			this.resultsError(error);
			this.hideSearchBoxExtended();
		});

		EventDispatcher.addEventListener('updateResultsSorting', (updatedSorting) => {
			// Handle reset sorting from filter modules (e.g. emission)
			// Fallback to the previous sorting selected by user if distinct, else fall back to default sorting (= price)
			if (!updatedSorting) {
				updatedSorting = (this.previousSorting !== this.searchParams.sorting) ? this.previousSorting : defaultSorting;
			}
			this.updateSorting(updatedSorting, true);
		});

		// Timetable and timeline merge search params
		EventDispatcher.addEventListener('updateResults', ({ source, params } = {}) => {
			if (this.activeView === source) {
				const searchParams = this.searchParamsForAPI({
					...params,
					sorting: this.searchParams.sorting,
					page: 0,
					id: urlVars.id,
					timestamp: timestamp,
					// Carefully merge filters
					filters: {
						...this.filters.get(),
						...params.filters
					}
				});
				this.resultsGetFirst(searchParams);
			}
		});
	},
	initIntersectionObserver () {
		bindIntersectionObserver(gebi('results-intersection-observer'), (entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting && !resultsFetcher.isLoading && this.searchParams.page < resultsRender?.response?.meta?.numberOfPages) {
					this.resultsGetNext();
					logEvent(events.scrollMoreResults, eventCategories.scroll);
				}
			});
		}, { root: null, rootMargin: '30%', threshold: 0.1 });
	},
	trackSearchEvent () {
		const sc_children = js_params.childrenAges.length;
		const sc_adults = js_params.adults;
		const sc_from = js_params.from.city;
		const sc_to = js_params.to.city;
		const sc_roundtrip = js_params.returntrip;
		const sc_leave = js_params.leavedate;
		let sc_home;
		if (sc_roundtrip > 0) {
			sc_home = js_params.homedate;
		}

		const sc_price = js_params.price;
		const sc_id = js_params.fbid;
		const sc_fromData = js_params.from;
		const sc_toData = js_params.to;
		const sc_tripData = [{ from: sc_fromData, to: sc_toData }];

		trackEvent.searchInfo(sc_children, sc_adults, sc_from, sc_to, sc_leave, sc_home, sc_id, sc_price, sc_tripData);
		trackEvent.searchCompleted();
	},
	/**
	 * @param {Object} config
	 * @param {string} [config.style = showResult]
	 * @param {string} [config.messageText = error.e9]
	 * @param {string} [config.buttonText = searchBox.search.searchBtnText]
	 * @param {Function} [config.callBack = this.showSearchBoxExtended]
	 */
	showDisplayMessage (config = {}) {
		config = {
			style: 'showResult',
			messageText: l('error.e9'),
			buttonText: l('searchBox.search.searchBtnText'),
			callBack: () => this.showSearchBoxExtended(),
			...config
		};
		if (typeof this.displayMessage === 'undefined') {
			this.displayMessage = new DisplayMessage(this.elems.resultsColumn, config);
			this.displayMessage.show();
		} else {
			this.displayMessage.update(config);
			this.displayMessage.show();
		}

		logEvent(events.observeNoResults, eventCategories.observe, config.messageText ? config.messageText : config);
	},
	saveFiltersToUrl () {
		// Filters
		const filters = deepCopy(this.searchParams.filters);
		delete filters.alternativeHashes;

		if (!isEmptyObject(filters)) {
			urlParameterSet('filter', Base64.encode(JSON.stringify(filters)));
		} else {
			urlParameterDelete('filter');
		}
	},
	/**
	 * Dispatches showresult:resultsUpdated. Additionally calls priceCalender.update
	 * @param {flight[]} flightsData
	 */
	emitResponseChanged (flightsData) {
		// Send the word out that new results have been fetched to timetable and timeline

		EventDispatcher.dispatch('showresult:resultsUpdated', flightsData);


		// // Update Price Calendar if available
		// setTimeout(() => {
		// 	this.priceCalendar?.update?.(this.searchParamsForAPI());
		// }, 300);
	},
	/**
	 * @param {Boolean} state
	 */
	setCarbonOffsetTabState (state) {
		qsa('.abTabNav__btn.sort-type[data-sort=carbonOffset] input').forEach((carbonOffsetTabNavInput) => {
			carbonOffsetTabNavInput.checked = state;
		});

		if (qs('.abTabNav__btn.sort-type.abTabNav__btn--active')) {
			if (state) {
				logEvent(events.filterCO2CompensateEnabled, eventCategories.filter);
			} else {
				logEvent(events.filterCO2CompensateDisabled, eventCategories.filter);
			}
		}
	},
	/**
	 * @param {string} sort
	 */
	updateSorting (sort) {
		if (sort === this.searchParams.sorting) {
			return;
		}

		this.previousSorting = this.searchParams.sorting;

		this.searchParams.sorting = sort;
		// Update the url
		urlParameterSet('sorting', this.searchParams.sorting);

		// Update the price calendar
		this.priceCalendar?.update?.({ sorting: sort });

		// Update the nav tab
		this.setSortingTabs(sort);

		if (window.scrollY > 0) {
			setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 200);
		}

		EventDispatcher.dispatch('updateFiltersSorting', sort);

		if (this.activeView !== 'timetable') {
			this.resultsGetFirst();
		}

		qs('.abTabNav--sortResultLarge').dataset.sort = sort;
	},
	showSearchBoxExtended () {
		window.scrollTo(0, 0);

		this.elems.searchBoxWrapper.classList.add('abShowResultSearchBox--visible');

		document.body.classList.add('no-scroll');

		if (!this.searchBox.html.classList.contains('abSearchBox--extended') && window.aboutToSearch !== 1) {
			logEvent(events.focusSearch, eventCategories.search);
		}

		this.searchBox.html.classList.add('abSearchBox--extended');
		this.dateSelector?.hide();
		this.blurAbBtnCards();


		if (document.body.style.position !== 'fixed') {
			document.body.style.position = 'fixed';
		}
	},
	/**
	 * @param {Number} timeout
	 */
	hideSearchBoxExtended (timeout = 0) {
		const inner = this.elems.searchBoxWrapper.querySelector('.abShowResultSearchBox__inner');
		inner.classList.remove('abShowResultSearchBox__inner--touch');
		inner.classList.add('abShowResultSearchBox__inner--slideUp');

		setTimeout(() => {
			const scrollY = document.body.style.top;
			this.elems.searchBoxWrapper.classList.remove('abShowResultSearchBox--visible');
			document.body.classList.remove('no-scroll');
			inner.classList.remove('abShowResultSearchBox__inner--slideUp');
			// Reset scroll position
			document.body.style.position = '';
			document.body.style.top = '';
			window.scrollTo(0, parseInt(scrollY || '0') * -1);
			this.searchBox.html.classList.remove('abSearchBox--extended');
			logEvent(events.unfocusSearch, eventCategories.search);
		}, timeout);
	},
	showDateSelector () {
		if (typeof this.dateSelector === 'undefined') {
			return;
		}
		this.elems.hero.classList.remove('priceCalendarHero--animated');

		this.dateSelector.show();

		this.priceCalendar?.open?.();
	},
	allowSearchBoxTouchMove () {
		let allow = true;

		if (!this.elems.searchBoxWrapper.classList.contains('abShowResultSearchBox--visible')) {
			allow = false;
		} else if (this.searchBox.html.classList.contains('abSearchBox--show-overlay')) {
			allow = false;
		} else if (this.searchBox.dateSelector.html.classList.contains('abDateSelector--visible')) {
			allow = false;
		}

		return allow;
	},
	/**
	 * @param {Number} type
	 * @param {Object} searchBoxData
	 * @param {String} searchBoxData.departDate
	 * @param {String} searchBoxData.from
	 * @param {String} searchBoxData.fromIata
	 * @param {Array} searchBoxData.fromIatas
	 * @param {Boolean} searchBoxData.fromMetro
	 * @param {String} searchBoxData.returnDate
	 * @param {String} searchBoxData.to
	 * @param {String} searchBoxData.toIata
	 * @param {Array} searchBoxData.toIatas
	 * @param {Boolean} searchBoxData.toMetro
	 */
	setHeaderFromToDates (type, searchBoxData) {
		const abFromToDates = gebi('abFromToDates');
		const from = qs('.fromIata');
		const to = qs('.toIata');
		const arrows = qs('.abHeader__fromToDatesArrow');

		if (!searchBoxData?.[0]) {
			return;
		}
		if (type !== 2) {
			const { departDate, returnDate, fromIata, toIata } = searchBoxData[0];

			if (from) {
				from.innerHTML = (this.isAllNumeric(fromIata)) ? this.truncateString(searchBoxData[0].from, 5) : fromIata;
			}

			if (to) {
				to.innerHTML = (this.isAllNumeric(toIata)) ? this.truncateString(searchBoxData[0].to, 5) : toIata + ',';
			}

			const getFormattedDate = (date) => {
				const dateObject = dateParseISO(date);
				const day = dateGetDate(dateObject);
				const monthNumber = dateGetMonth(dateObject);
				const month = js_params.months[monthNumber];
				return `${day} ${month}`;
			};

			let datesString = getFormattedDate(departDate);

			if (type === 1 && returnDate) {
				datesString += ` - ${getFormattedDate(returnDate)}`;
			}

			qs('.dates', abFromToDates).innerHTML = datesString;

			if (arrows) {
				arrows.classList.add(type === 0 ? 'icon-arrow-right' : 'icon-switch');
			}
		} else {
			const fromIata = searchBoxData[0].fromIata;
			const toIata = searchBoxData.slice(-1)[0].toIata;
			const nbLegs = searchBoxData.length;

			if (from) {
				from.innerHTML = (this.isAllNumeric(fromIata)) ? this.truncateString(searchBoxData[0].from, 5) : fromIata;
			}

			if (to) {
				to.innerHTML = (this.isAllNumeric(toIata)) ? this.truncateString(searchBoxData.slice(-1)[0].to, 5) : toIata;
			}

			const icon = 'icon-openjaw-' + nbLegs;

			if (arrows) {
				arrows.classList.add(icon);
			}
		}
	},
	blurAbBtnCards () {
		const viewBtns = qs('.abBtnCard--container');
		const activeBtn = qs('active');

		viewBtns?.classList?.remove?.('active');

		activeBtn?.classList?.remove?.('active');
	},
	generateTempResult (previewGroup) {
		const journeys = [];
		const wrapper = {};

		// do not add more results if we already have a lot
		if (!previewGroup || qsa('#results-container > .abResult').length > 15) {
			return;
		}

		let { segments, trips, flights } = previewGroup;


		// TODO: get backend to return agents as an array
		flights = flights.map((flight) => {
			flight.agents = [{
				agent: flight.agent.name,
				code: flight.agent.code,
				price: parseInt(flight.agent.minprice),
				stop: 0
			}];
			return flight;
		});


		resultsRender.response.flights = flights;
		resultsRender.response.segments = segments;
		resultsRender.response.trips = trips;

		for (let i = 0; i < flights.length && i < 10; ++i) {
			journeys.push(resultsRender.getFlightRenderData(flights[i], i, false));
		}

		wrapper.journeys = journeys;
		wrapper.preview = true;

		const html = Handlebars.templates.result(wrapper);

		this.elems.resultsContainer.insertAdjacentHTML('afterbegin', html);
	},
	/**
	 *
	 * @param {number} interval
	 */
	setupPreviewResultsTimeout (interval) {
		this.clearPreviewResultsInterval();

		this.previewResultsInterval = setInterval(() => {
			const [lastHiddenResult] = qsa('.abResult:not(.visible)', this.elems.resultsContainer).slice(-1);
			if (!lastHiddenResult) {
				this.clearPreviewResultsInterval();
				return;
			}
			slideDown(lastHiddenResult);
			lastHiddenResult.classList.add('visible');
			qsa('.abResult.visible', this.elems.resultsContainer).filter((item, index) => index > 6).forEach((element) => {
				element.remove();
			});
		}, interval);
	},
	clearPreviewResultsInterval () {
		clearInterval(this.previewResultsInterval);
	},
	/**
	 *
	 * @param {string} sortBy
	 */
	setSortingTabs (sortBy) {
		// price traveltime bestValue

		// Dont include landing setup for events
		if (qs('.abTabNav__btn.sort-type.abTabNav__btn--active')) {
			if (sortBy === 'price') {
				logEvent(events.sortCheapest, eventCategories.sort);
			} else if (sortBy === 'traveltime') {
				logEvent(events.sortQuickest, eventCategories.sort);
			} else {	// bestValue
				logEvent(events.sortBest, eventCategories.sort);
			}
		}

		this.elems.sortBars.forEach((btn) => {
			btn.classList.toggle('abTabNav__btn--active', btn.dataset.sort === sortBy);
		});
	},
	renderNoMoreResults () {
		if (qs('#no-more-results', this.elems.resultsContainer)) {
			return;
		}

		const html = `
<div id="no-more-results" class="results-container__footer">
	<div>
		<span>
			<img src="/static/v${js_params.version}/images/global/round-flygresor.png" alt="">
			${l('showresult.noMoreResults')}
		</span>
	</div>
	<span>${l('showresult.noMoreResultsDescription')}</span>
</div>
`;

		this.elems.resultsContainer.insertAdjacentHTML('beforeend', html);
	},
	async offlineFooterButtonEvent () {
		await this.resultsGetNext(true);
		if (!resultsFetcher.isError) {
			gebi('#results-offline')?.remove?.();
		}
	},
	offlineFooterRender () {
		if (qs('#results-offline', this.elems.resultsContainer) || this.searchParams.page === resultsRender?.response?.meta?.numberOfPages) {
			return;
		}

		const html = `
<div id="results-offline" class="results-container__footer results-container__footer--offline" style="display:none">
	<div>
		<span class="header">
			${l('Network error')}
		</span>
	</div>
	<div>
		<span>${l('error.e14')}</span>
	</div>
</div>
		`;

		this.elems.resultsContainer.insertAdjacentHTML('beforeend', html);
	},
	showResetFiltersMsg () {
		const config = {
			messageText: l('showresult.info.allResultsAreFiltered'),
			buttonText: l('showresult.filter.removeBtnText'),
			callBack: () => this.filters.reset(this.isYouthSearch)
		};

		this.showDisplayMessage(config);
	},
	showNoYouthTicketsWarning () {
		// Boot up no youth ticket warning message to user
		const noYouthTicketWarning = gebi('results-warning');
		// Dynamically set height for closing animation
		noYouthTicketWarning.classList.remove('hidden');
		const resizeMessage = () => {
			noYouthTicketWarning.style.height = noYouthTicketWarning.querySelector('.abDisplayMessage').offsetHeight + 'px';
		};
		resizeMessage();
		window.addEventListener('resize', resizeMessage);
		// Bind close button click event
		const onButtonClick = (event) => {
			event.stopPropagation();
			noYouthTicketWarning.style.height = '0px';
			window.removeEventListener('resize', resizeMessage);
			setTimeout(() => {
				noYouthTicketWarning.parentElement.removeChild(noYouthTicketWarning);
			}, 1000);
		};
		noYouthTicketWarning.querySelector('.abDisplayMessage__btn').addEventListener('click', onButtonClick);

		// Fetch other available ticket matching query
		this.isYouthSearch = false;
		delete this.searchParams.filters.youthTicket;
		EventDispatcher.dispatch('disableYouthFilter');
	},
	/**
	 * @param {Object} sortingPrices
	 * @param {Number} sortingPrices.cheapest
	 * @param {Number} sortingPrices.fastest
	 * @param {Number} sortingPrices.best
	 * @param {Number} sortingPrices.emission
	 */
	updateSortingTabs () {
		const data = {
			cheapest: {
				price: resultsRender?.response?.sortingButtons?.cheapest,
				traveltime: resultsRender?.response?.meta?.sortingButtonsTravelTime?.cheapest
			},
			fastest: {
				price: resultsRender?.response?.sortingButtons?.fastest,
				traveltime: resultsRender?.response?.meta?.sortingButtonsTravelTime?.fastest
			},
			best: {
				price: resultsRender?.response?.sortingButtons?.best,
				traveltime: resultsRender?.response?.meta?.sortingButtonsTravelTime?.best
			},
			emission: {
				price: resultsRender?.response?.sortingButtons?.emission,
				traveltime: resultsRender?.response?.meta?.sortingButtonsTravelTime?.emission
			}
		};

		// Loop through data and call lc and traveltime functions
		for (const key in data) {
			data[key].price = data[key].price > 0 ? lc(data[key].price) : '';
			data[key].traveltime = data[key].traveltime > 0 ? getTimeText(data[key].traveltime) : '';
		}

		const indexedObject = Object.values(data);

		this.elems.sortBars.forEach((btn, index) => {
			index = index % indexedObject.length;
			// We only hide the carbon checkbox, the rest are here for consistancy
			if (btn.dataset.sort === 'carbonOffset') {
				btn.classList.toggle('hidden', !indexedObject[index].price);
			} else {
				btn.classList.toggle('disabled', !indexedObject[index].price);
			}

			if (indexedObject?.[index]?.price) {
				btn.querySelector('.sort-price').innerHTML = indexedObject?.[index].price;
			} else {
				btn.querySelector('.sort-price').innerHTML = l('global.simplePhrases.unavailable');
			}

			const timeButton = qs('.sort-time', btn);
			if (timeButton) {
				timeButton.innerHTML = indexedObject?.[index]?.traveltime;
			}
		});
	},

	/**
	 *
	 * @param {String} str
	 * @returns {Boolean}
	 */
	isAllNumeric: str => str.match(/^[0-9]+$/),
	/**
	 * @param {String} str
	 * @param {Number} num
	 * @returns
	 */
	truncateString: (str, num) => str.length > num ? str.slice(0, num) + '.' : str
};

window.showresult = showresult;

documentReady(function () {
	showresult.init();
});
